import "./ProyectosCommon.css";
import { BaseSmallModalFormComponent } from "../../../shared/components/modal/BaseSmallModalFormComponent";
import { SnapshotResponse } from "../../../shared/response/proyectos/SnapshotResponse";
import { SnapshotPayload } from "../../../shared/services/api/payload/proyectos/SnapshotPayload";
import { SnapshotSaveService } from "../../services/proyectos/SnapshotSaveService";
import { CENTAVOS_POR_PESO, centavosAPesos } from "../../../shared/utils/proyectosFormat";

/**
 * Modal crear/editar snapshot macroeconómico. El usuario ingresa montos en **pesos** y el
 * IPC en **porcentaje**; se convierten a centavos / decimal para el payload. `factorJornal`
 * lo calcula el backend. Al crear un snapshot nuevo el backend NO lo activa — eso es acción aparte.
 */
export class SnapshotFormModal extends BaseSmallModalFormComponent<
  SnapshotResponse,
  SnapshotPayload,
  SnapshotSaveService
> {
  private _año!:      HTMLInputElement;
  private _smmlv!:    HTMLInputElement;
  private _subsidio!: HTMLInputElement;
  private _ipc!:      HTMLInputElement;
  private _nota!:     HTMLTextAreaElement;

  constructor(
    private readonly _onSavedCb: (item: SnapshotResponse) => void,
    private readonly _item?: SnapshotResponse,
  ) {
    super(new SnapshotSaveService());
  }

  protected get title(): string { return this._item ? "Editar snapshot" : "Nuevo snapshot"; }
  protected get saveText(): string { return "Guardar snapshot"; }

  protected renderBody(): string {
    const c = this._item;
    const smmlv = c ? centavosAPesos(c.smmlv) : "";
    const subsidio = c ? centavosAPesos(c.subsidioTransporte) : "";
    const ipc = c ? (Number(c.ipc) * 100) : "";
    return `
      <div class="cl-form-fields">
        <div class="pr-form-row">
          <div class="cl-form-group">
            <label class="cl-form-label" for="snp-año">Año <span class="cl-required">*</span></label>
            <input id="snp-año" class="sx-input sx-input--numeric" type="number" min="2000" step="1" value="${c?.año ?? new Date().getFullYear()}">
          </div>
          <div class="cl-form-group">
            <label class="cl-form-label" for="snp-ipc">IPC (%) <span class="cl-required">*</span></label>
            <input id="snp-ipc" class="sx-input sx-input--numeric" type="number" min="0" step="0.01" value="${ipc}" placeholder="6.5">
          </div>
        </div>

        <div class="cl-form-group">
          <label class="cl-form-label" for="snp-smmlv">SMMLV mensual (COP) <span class="cl-required">*</span></label>
          <input id="snp-smmlv" class="sx-input sx-input--numeric" type="number" min="0" step="1" value="${smmlv}" placeholder="1423500">
          <span class="pr-form-note">El factor jornal lo calcula el backend a partir del SMMLV.</span>
        </div>

        <div class="cl-form-group">
          <label class="cl-form-label" for="snp-subsidio">Subsidio de transporte (COP)</label>
          <input id="snp-subsidio" class="sx-input sx-input--numeric" type="number" min="0" step="1" value="${subsidio}" placeholder="200000">
        </div>

        <div class="cl-form-group">
          <label class="cl-form-label" for="snp-nota">Nota</label>
          <textarea id="snp-nota" class="sx-input" rows="2" style="resize:vertical" placeholder="Parámetros vigentes…">${this._esc(c?.nota ?? "")}</textarea>
        </div>
      </div>
    `;
  }

  protected override onAfterRender(el: HTMLDivElement): void {
    this._año      = el.querySelector<HTMLInputElement>("#snp-año")!;
    this._smmlv    = el.querySelector<HTMLInputElement>("#snp-smmlv")!;
    this._subsidio = el.querySelector<HTMLInputElement>("#snp-subsidio")!;
    this._ipc      = el.querySelector<HTMLInputElement>("#snp-ipc")!;
    this._nota     = el.querySelector<HTMLTextAreaElement>("#snp-nota")!;

    [this._año, this._smmlv, this._ipc].forEach((i) => i.addEventListener("input", () => this._validate()));
    this._validate();
  }

  protected buildPayload(): SnapshotPayload {
    return {
      id: this._item?.id ?? null,
      año: Math.floor(Number(this._año.value) || 0),
      smmlv: Math.round((Number(this._smmlv.value) || 0) * CENTAVOS_POR_PESO),
      subsidioTransporte: Math.round((Number(this._subsidio.value) || 0) * CENTAVOS_POR_PESO),
      ipc: (Number(this._ipc.value) || 0) / 100,
      nota: this._nota.value.trim() || null,
    };
  }

  protected override onSaved(item: SnapshotResponse): void {
    this._onSavedCb(item);
    this.dispose();
  }

  private _validate(): void {
    const ok = Number(this._año.value) >= 2000
      && Number(this._smmlv.value) > 0
      && this._ipc.value.trim() !== "";
    this.setSaveDisabled(!ok);
  }

  private _esc(s: string): string {
    return (s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
}
